const createSafeDate = (
	year: number,
	month: number,
	day: number,
): Date | null => {
	if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
		return null;
	}

	const date = new Date(year, month - 1, day);

	if (
		date.getFullYear() !== year ||
		date.getMonth() !== month - 1 ||
		date.getDate() !== day
	) {
		return null;
	}

	return date;
};

const normalizeYear = (year: number): number => {
	if (!Number.isInteger(year)) {
		return year;
	}

	// Support YY format from API payloads (e.g. 26 -> 2026).
	if (year >= 0 && year <= 99) {
		return 2000 + year;
	}

	return year;
};

const parseDateString = (dateString: string): Date | null => {
	if (!dateString || typeof dateString !== "string") {
		return null;
	}

	const parts = dateString.trim().split("/");

	if (parts.length === 3) {
		const [firstPart, secondPart, rawYearPart] = parts.map((value) =>
			parseInt(value.trim(), 10),
		);
		const yearPart = normalizeYear(rawYearPart);

		// Prefer DD/MM/YYYY as intake API commonly sends this format.
		const dayMonthYearDate = createSafeDate(yearPart, secondPart, firstPart);
		if (dayMonthYearDate) {
			return dayMonthYearDate;
		}

		// Fallback for MM/DD/YYYY inputs.
		return createSafeDate(yearPart, firstPart, secondPart);
	}

	if (parts.length === 2) {
		const [monthPart, rawYearPart] = parts.map((value) =>
			parseInt(value.trim(), 10),
		);
		const yearPart = normalizeYear(rawYearPart);

		// MM/YYYY format, day defaults to first day of month.
		return createSafeDate(yearPart, monthPart, 1);
	}

	return null;
};

const getClosestFutureDateMeta = (
	dateStrings: string[],
): { index: number; date: Date } | null => {
	if (!Array.isArray(dateStrings) || dateStrings.length === 0) {
		return null;
	}

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	let closestDate: Date | null = null;
	let closestDateIndex = -1;
	let minDifference = Number.POSITIVE_INFINITY;

	dateStrings.forEach((dateString, index) => {
		const date = parseDateString(dateString);
		if (!date) {
			return;
		}

		if (date >= today) {
			const difference = date.getTime() - today.getTime();
			if (difference < minDifference) {
				minDifference = difference;
				closestDate = date;
				closestDateIndex = index;
			}
		}
	});

	if (!closestDate || closestDateIndex === -1) {
		return null;
	}

	return { index: closestDateIndex, date: closestDate };
};

export const getClosestFutureDateIndex = (dateStrings: string[]): number => {
	const closestMeta = getClosestFutureDateMeta(dateStrings);
	return closestMeta ? closestMeta.index : -1;
};

export const getClosestFutureDate = (dateStrings: string[]): string => {
	const closestMeta = getClosestFutureDateMeta(dateStrings);
	return closestMeta ? formatDate(closestMeta.date) : "";
};

const formatDate = (date: Date): string => {
	return date.toLocaleString("en-US", { month: "long", year: "numeric" });
};

export default formatDate;
